import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    Code2,
    Server,
    Shield,
    Brain,
    Megaphone,
    Home,
    FolderArchive,
    ArrowUpRight,
} from "lucide-react";
import { useI18n } from "../i18n";
import { SERVICE_SLUGS } from "../data/serviceDetails";

const ICONS = {
    dev: Code2,
    it: Server,
    sec: Shield,
    ai: Brain,
    mkt: Megaphone,
    iot: Home,
    doc: FolderArchive,
};

export default function Services() {
    const { dict, t } = useI18n();
    const cats = dict.services.cats;

    return (
        <section
            id="services"
            data-testid="services-section"
            className="section-pad relative scroll-mt-24"
        >
            <div className="container-page">
                <div className="mb-14 max-w-3xl">
                    <span className="eyebrow">{t("services.eyebrow")}</span>
                    <h2
                        data-testid="services-title"
                        className="mt-5 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter whitespace-pre-line"
                    >
                        {t("services.title")}
                    </h2>
                    <p className="mt-5 text-white/60 text-base md:text-lg max-w-2xl">
                        {t("services.subtitle")}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {cats.map((cat, idx) => {
                        const Icon = ICONS[cat.key] || Code2;
                        const isFeatured = idx === 0 || idx === 4;
                        return (
                            <motion.article
                                key={cat.key}
                                data-testid={`service-card-${cat.key}`}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.5, delay: idx * 0.05 }}
                                className={`group relative card-surface p-7 overflow-hidden ${
                                    isFeatured ? "lg:row-span-1" : ""
                                }`}
                            >
                                {/* top beam */}
                                <span className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-brand to-transparent opacity-0 group-hover:opacity-80 transition-opacity duration-500" />
                                {/* hover glow */}
                                <span className="absolute -top-24 -right-24 h-48 w-48 rounded-full bg-brand/20 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                                <div className="relative z-10 flex items-start justify-between">
                                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-brand-glow group-hover:border-brand/40 transition-colors">
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <ArrowUpRight className="h-4 w-4 text-white/30 group-hover:text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                                </div>

                                <h3 className="relative z-10 mt-6 text-xl md:text-2xl font-semibold tracking-tight">
                                    {cat.title}
                                </h3>
                                <p className="relative z-10 mt-2 text-sm text-white/55 leading-relaxed">
                                    {cat.desc}
                                </p>

                                <ul className="relative z-10 mt-5 space-y-1.5">
                                    {cat.items.map((it) => (
                                        <li
                                            key={it}
                                            className="text-xs text-white/45 flex items-center gap-2"
                                        >
                                            <span className="h-1 w-1 rounded-full bg-brand-glow/70" />
                                            {it}
                                        </li>
                                    ))}
                                </ul>

                                <Link
                                    to={`/servicios/${SERVICE_SLUGS[cat.key]}`}
                                    aria-label={cat.title}
                                    data-testid={`service-card-link-${cat.key}`}
                                    className="absolute inset-0 z-20"
                                />

                                <Link
                                    to={`/servicios/${SERVICE_SLUGS[cat.key]}`}
                                    data-testid={`service-cta-${cat.key}`}
                                    className="relative z-30 mt-6 inline-flex items-center gap-1.5 text-xs font-medium text-white/70 hover:text-white"
                                >
                                    {t("services.cta")}
                                    <ArrowUpRight className="h-3 w-3" />
                                </Link>
                            </motion.article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
